<template>
  <WdsText>
    Upload a
    <code>.docx</code>
    template and preview it. Once a file is selected, we’ll show a split view with the live Word
    preview on the left and the generated PDF on the right.
  </WdsText>

  <div
    ref="splitWrap"
    :class="['preview-wrap', { 'is-split': !!selectedFile, 'is-dragging': isResizing }]"
    :style="splitStyle"
  >
    <div class="col left">
      <WdsText weight="bold">Word template preview (client-side)</WdsText>

      <div
        ref="docxStage"
        class="docx-dropzone"
        :class="{ 'is-dragover': isDragOver }"
        @click="openFileDialog"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <input
          ref="fileInput"
          type="file"
          accept=".docx"
          class="hidden-input"
          @change="onFileChange"
        />
        <div v-if="!selectedFile" class="dz-placeholder">
          <div class="dz-instructions">
            <div class="dz-title">Drag & drop a .docx here</div>
            <div class="dz-subtitle">or click to choose a file</div>
          </div>
        </div>
        <div ref="docxScale" class="docx-scale"></div>
        <div v-if="isRenderingDocx" class="overlay">
          <div class="spinner" aria-label="Rendering Word document…"></div>
          <div class="overlay-text">Rendering Word preview…</div>
        </div>
      </div>

      <div v-if="hasFloating" class="callout warn">
        Heads up: this template contains floating text boxes/shapes. The browser preview may
        paginate them differently. Consider using a header table or setting objects to “In line with
        text” for best fidelity.
      </div>

      <div class="actions">
        <WdsButton :disabled="!selectedFile || isGeneratingPdf" @click="submit">
          <template v-if="!isGeneratingPdf">Generate PDF</template>
          <template v-else>Generating PDF…</template>
        </WdsButton>
        <WdsButton
          variant="secondary"
          :disabled="!selectedFile || isGeneratingPdf"
          @click="clearFile"
        >
          Clear
        </WdsButton>
      </div>
    </div>

    <!-- HANDLE: only visible when split -->
    <div v-if="selectedFile" class="resize-handle" @pointerdown="onResizeStart"></div>

    <!-- RIGHT: PDF -->
    <div v-show="selectedFile" class="col right">
      <WdsText weight="bold">Finalized PDF (server result)</WdsText>
      <div class="pdf-frame">
        <div v-if="isGeneratingPdf" class="overlay">
          <div class="spinner" aria-label="Generating PDF…"></div>
          <div class="overlay-text">Generating PDF…</div>
        </div>
        <iframe v-if="raw" :src="raw" class="pdf-iframe" />
        <div v-else class="pdf-placeholder">No PDF yet</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WdsButton, WdsText } from '@worksome/wds-components'
import { ref, nextTick, onMounted, onBeforeUnmount, computed } from 'vue'
import { renderAsync } from 'docx-preview'
import JSZip from 'jszip'

/** State */
const fileInput = ref<HTMLInputElement | null>(null)
const selectedFile = ref<File | null>(null)
const raw = ref<string | null>(null)

const docxStage = ref<HTMLDivElement | null>(null)
const docxScale = ref<HTMLDivElement | null>(null)

const isRenderingDocx = ref(false)
const isGeneratingPdf = ref(false)
const isDragOver = ref(false)
const hasFloating = ref(false)

let ro: ResizeObserver | null = null
let mo: MutationObserver | null = null

// Split/resize state
const splitWrap = ref<HTMLDivElement | null>(null)
const isResizing = ref(false)
const leftWidth = ref(640) // starting width (px) of left column when split
const HANDLE_W = 8 // handle thickness (px)
const MIN_LEFT = 380 // don't let Word panel get too small
const MIN_RIGHT = 560 // keep PDF usable

const splitStyle = computed(() =>
  // When not split, we keep your single-column layout (CSS below handles that).
  selectedFile.value
    ? { gridTemplateColumns: `${leftWidth.value}px ${HANDLE_W}px 1fr` }
    : undefined,
)

// Keep left width valid on window resize
function clampLeftWithinContainer() {
  if (!splitWrap.value) return
  const wrapW = splitWrap.value.clientWidth
  const maxLeft = Math.max(MIN_LEFT, wrapW - HANDLE_W - MIN_RIGHT)
  leftWidth.value = Math.min(Math.max(leftWidth.value, MIN_LEFT), maxLeft)
}

function onResizeStart(e: PointerEvent) {
  if (!splitWrap.value) return
  isResizing.value = true
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)

  const startX = e.clientX
  const startLeft = leftWidth.value

  const onMove = (ev: PointerEvent) => {
    const dx = ev.clientX - startX
    const wrapW = splitWrap.value!.clientWidth
    const maxLeft = Math.max(MIN_LEFT, wrapW - HANDLE_W - MIN_RIGHT)
    leftWidth.value = Math.min(Math.max(startLeft + dx, MIN_LEFT), maxLeft)
    // docxStage width changes -> ResizeObserver already calls fitDocxToWidth()
  }
  const onUp = (ev: PointerEvent) => {
    isResizing.value = false
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
  }

  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp)
}

/** Helpers */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(String(reader.result))
    reader.readAsDataURL(blob)
  })
}

async function detectFloating(file: File) {
  try {
    const zip = await JSZip.loadAsync(file)
    const targets = Object.keys(zip.files).filter((p) =>
      /^word\/(document|header\d+)\.xml$/.test(p),
    )
    const needles = ['<wp:anchor', '<v:shape', '<w:pict']
    for (const p of targets) {
      const xml = await zip.files[p].async('string')
      if (needles.some((n) => xml.includes(n))) {
        hasFloating.value = true
        return
      }
    }
    hasFloating.value = false
  } catch {
    hasFloating.value = false
  }
}

function fitDocxToWidth() {
  if (!docxStage.value || !docxScale.value) return
  const page = docxScale.value.querySelector('.docx .page') as HTMLElement | null
  const docRoot = (page ?? docxScale.value.querySelector('.docx')) as HTMLElement | null
  if (!docRoot) return

  const cs = getComputedStyle(docxStage.value)
  const paddingX = parseFloat(cs.paddingLeft || '0') + parseFloat(cs.paddingRight || '0')
  const available = docxStage.value.clientWidth - paddingX - 1 // rounding guard
  const pageWidth = (page?.offsetWidth ?? docRoot.offsetWidth) || 816
  const scale = Math.min(1, available / pageWidth)
  docxStage.value.style.setProperty('--docx-scale', String(scale))

  const rawHeight = docxScale.value.scrollHeight
  const scaledHeight = Math.ceil(rawHeight * scale)
  docxStage.value.style.setProperty('--docx-height', `${scaledHeight}px`)
}

async function renderDocxPreview(file: File) {
  if (!docxScale.value) return
  isRenderingDocx.value = true
  docxScale.value.innerHTML = ''

  await renderAsync(file, docxScale.value, undefined, {
    className: 'docx',
    inWrapper: true,
    ignoreFonts: false,
  })

  await nextTick()
  fitDocxToWidth()
  isRenderingDocx.value = false

  // observe for late content
  if (mo) mo.disconnect()
  mo = new MutationObserver(() => fitDocxToWidth())
  mo.observe(docxScale.value, { childList: true, subtree: true, attributes: true })
  docxScale.value.querySelectorAll('img').forEach((img: Element) => {
    const el = img as HTMLImageElement
    if (!el.complete) el.addEventListener('load', fitDocxToWidth, { once: true })
  })
}

/** Drag & drop + file picking */
function openFileDialog() {
  fileInput.value?.click()
}
function onDragOver() {
  isDragOver.value = true
}
function onDragLeave() {
  isDragOver.value = false
}

async function handleNewFile(file: File) {
  selectedFile.value = file
  raw.value = null
  await detectFloating(file)
  await nextTick()
  clampLeftWithinContainer()
  await renderDocxPreview(file)
}

async function onDrop(e: DragEvent) {
  isDragOver.value = false
  const file = e.dataTransfer?.files?.[0] || null
  if (!file || !file.name.toLowerCase().endsWith('.docx')) return
  await handleNewFile(file)
}
async function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input?.files?.[0] || null
  if (!file) return
  await handleNewFile(file)
}

/** Server PDF */
async function submit() {
  if (!selectedFile.value || isGeneratingPdf.value) return
  isGeneratingPdf.value = true
  try {
    const form = new FormData()
    form.append('file', selectedFile.value)
    const res = await fetch('docx/post', {
      method: 'POST',
      headers: {
        'X-CSRF-TOKEN':
          document.querySelector('meta[name="_token"]')?.getAttribute('content') ?? '',
      },
      body: form,
    })
    const blob = await res.blob()
    raw.value = await blobToDataUrl(blob)
  } finally {
    isGeneratingPdf.value = false
  }
}

function clearFile() {
  selectedFile.value = null
  raw.value = null
  hasFloating.value = false
  if (docxScale.value) docxScale.value.innerHTML = ''
}

onMounted(() => {
  ro = new ResizeObserver(() => fitDocxToWidth())
  if (docxStage.value) ro.observe(docxStage.value)
  window.addEventListener('resize', fitDocxToWidth)
  window.addEventListener('resize', clampLeftWithinContainer)
})

onBeforeUnmount(() => {
  mo?.disconnect()
  ro?.disconnect()
  window.removeEventListener('resize', fitDocxToWidth)
  window.removeEventListener('resize', clampLeftWithinContainer)
})
</script>

<style scoped>
/* Layout: single column by default; when split we use 3 columns: left | handle | right */
.preview-wrap {
  display: grid;
  grid-template-columns: 1fr; /* single column until a file exists */
  gap: 24px;
  margin-top: 16px;
  align-items: start;
}
.preview-wrap.is-split {
  /* columns are set inline via :style="splitStyle" */
  gap: 0; /* handle has its own visual separation */
}
.col {
  min-width: 0;
  padding-right: 24px;
}
.preview-wrap.is-split .col.left {
  padding-right: 12px;
}
.preview-wrap.is-split .col.right {
  padding-left: 12px;
}

/* Handle between panes */
.resize-handle {
  width: 8px;
  cursor: col-resize;
  position: relative;
  height: 700px;
}
.resize-handle::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, transparent 0, #eee 50%, transparent 100%);
}
.preview-wrap.is-dragging,
.preview-wrap.is-dragging * {
  cursor: col-resize !important;
  user-select: none;
}

/* Responsive: collapse to single column on smaller screens */
@media (max-width: 1200px) {
  .preview-wrap.is-split {
    grid-template-columns: 1fr !important;
  }
  .resize-handle {
    display: none;
  }
  .preview-wrap.is-split .col.left,
  .preview-wrap.is-split .col.right {
    padding: 0 0 24px 0;
  }
}
/* Dropzone / Word preview container */
.docx-dropzone {
  --docx-scale: 1;
  --docx-height: 600px;

  position: relative;
  height: 680px;
  padding: 16px;
  border: 2px dashed #d8dde6;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  overflow: auto;
  overflow-x: hidden;
  transition:
    border-color 120ms ease,
    background 120ms ease;
}
.docx-dropzone.is-dragover {
  border-color: #8aa2ff;
  background: #f8faff;
}

.hidden-input {
  display: none;
}

.dz-placeholder {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
}
.dz-title {
  font-weight: 600;
}
.dz-subtitle {
  font-size: 12px;
  opacity: 0.7;
}

/* Scaled content: absolutely positioned; spacer defines scroll height */
.docx-scale {
  position: absolute;
  inset: 0 auto auto 0;
  transform: scale(var(--docx-scale));
  transform-origin: top left;
}
.docx-dropzone::after {
  content: '';
  display: block;
  height: var(--docx-height);
}

/* PDF panel */
.pdf-frame {
  position: relative;
  border: 1px solid #eee;
  border-radius: 12px;
  background: #fff;
  height: 680px;
  overflow: hidden;
}
.pdf-iframe {
  width: 100%;
  height: 100%;
  border: 0;
}
.pdf-placeholder {
  height: 100%;
  display: grid;
  place-items: center;
  color: #888;
}

/* Callouts / actions / overlays */
.callout.warn {
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid #f3c78c;
  background: #fff7e6;
  border-radius: 10px;
  font-size: 14px;
}
.actions {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
}

.overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.7);
  z-index: 1;
  text-align: center;
}
.overlay-text {
  margin-top: 8px;
  font-size: 12px;
  color: #333;
}
.spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid #d9d9d9;
  border-top-color: #6b7cff;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
